export const LEVEL_COUNT = 50;
const BONUS_EVERY = 10;
/**
 * Генерує конфігурацію для рівня `id` (1..50) за фіксованою балансною кривою.
 * Чисто детерміновано, без RNG зі сторони — той самий id завжди дає той самий конфіг.
 *
 * Ескалація:
 *  • soft-щільність повільно росте з рівнем (0.40 → 0.55)
 *  • pickup-ів стає більше з рівнем
 *  • Balloom присутні завжди, далі домішуються Onil → Pass
 *  • тайм-ліміт повільно скорочується
 *  • кожен 10-й рівень (10, 20, 30, 40, 50) — bonus stage із зграєю Balloom-ів
 *    і без exit (виграш = вибив усіх або вийшов час)
 */
export function buildLevel(id) {
    const clamped = Math.max(1, Math.min(LEVEL_COUNT, id | 0));
    const isBonus = clamped % BONUS_EVERY === 0;
    const softDensity = Math.min(0.55, 0.40 + clamped * 0.003);
    const pickupCount = Math.min(6, 2 + Math.floor(clamped / 10));
    const timeLimit = isBonus ? 30 : Math.max(90, 200 - clamped * 2);
    const enemies = isBonus
        ? bonusEnemies(clamped)
        : campaignEnemies(clamped);
    return {
        id: clamped,
        seed: 0xb0b0 ^ (clamped * 0x9e3779b1),
        softDensity,
        enemies,
        pickupCount,
        timeLimit,
        bonus: isBonus,
    };
}
function campaignEnemies(level) {
    // Базова кількість росте лінійно
    const total = Math.min(12, 4 + Math.floor(level / 3));
    const out = new Map();
    const add = (k, n) => out.set(k, (out.get(k) ?? 0) + n);
    if (level <= 4) {
        add('balloom', total);
    }
    else if (level <= 9) {
        const onil = Math.max(1, Math.floor(total * 0.3));
        add('onil', onil);
        add('balloom', total - onil);
    }
    else if (level <= 14) {
        const onil = Math.floor(total * 0.5);
        add('onil', onil);
        add('balloom', total - onil);
    }
    else if (level <= 19) {
        const pass = 1;
        const onil = Math.floor((total - pass) * 0.6);
        add('pass', pass);
        add('onil', onil);
        add('balloom', total - pass - onil);
    }
    else {
        const pass = Math.min(3, 1 + Math.floor((level - 20) / 5));
        const onil = Math.floor((total - pass) * 0.5);
        add('pass', pass);
        add('onil', onil);
        add('balloom', total - pass - onil);
    }
    return [...out.entries()]
        .filter(([, c]) => c > 0)
        .map(([type, count]) => ({ type, count }));
}
function bonusEnemies(level) {
    // Бонус-стейдж: багато Balloom-ів, на пізніх рівнях додаємо Onil
    const total = 8 + Math.floor(level / 5);
    if (level < 30)
        return [{ type: 'balloom', count: total }];
    return [
        { type: 'balloom', count: Math.floor(total * 0.7) },
        { type: 'onil', count: total - Math.floor(total * 0.7) },
    ];
}
/** Розгорнути [{type:'balloom', count:3}, …] у плоский список для спавнера. */
export function flattenEnemies(enemies) {
    const out = [];
    for (const e of enemies)
        for (let i = 0; i < e.count; i++)
            out.push(e.type);
    return out;
}
